'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment } from 'react';

import { addToCart } from '@eliadmin/core/src/api/cart/cart-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import {
  cartReadiness,
  productCount,
} from '@eliadmin/core/src/components/dlz/account/favorites/favorites';
import { FAVORITES_COPY } from '@eliadmin/core/src/components/dlz/account/favorites/favorites-copy';
import { FavoritesNotice } from '@eliadmin/core/src/components/dlz/account/favorites/favorites-notice';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { getProductUrl, normalizeProductQuantity } from '@eliadmin/core/src/utils/product-utils';

export type AddAllResult = {
  added: Product[];
  /** Products with variants: the customer has to pick one on the product page. */
  needsVariant: Product[];
  unavailable: Product[];
  failed: Product[];
};

/** ISO code of the shop currency for analytics – the lookup of the core cart button. */
function useCurrencyIso() {
  const locale = useLocaleStore(s => s.currentLocale);
  const symbol = useGlobalStore(s => s.generalLayout.currency);
  const { data } = useQuery({
    queryKey: [CacheKey.CURRENCIES, locale],
    queryFn: () => fetchCurrencies({ locale }),
  });
  return data?.data.find(currency => currency.symbol === symbol)?.id || symbol;
}

/**
 * „Pridať všetko do košíka“: every favourite that needs no choice goes to the cart with the
 * request, quantity and analytics event of the core cart button (`addToCart`, minimum quantity).
 * Products with variants are left out – a favourite is a product, not a variant, and adding a
 * guessed variant could put the wrong size in the cart – and so are products that cannot be bought
 * now. The requests run one after another (one cart); the cart, shipments and payments are
 * refreshed once after the batch instead of after every product.
 */
export function useAddAllToCart() {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const queryClient = useQueryClient();
  const currency = useCurrencyIso();

  return useMutation({
    meta: { affectsCart: true },
mutationFn: async (products: Product[]) => {
      const result: AddAllResult = { added: [], needsVariant: [], unavailable: [], failed: [] };
      for (const product of products) {
        const readiness = cartReadiness(product);
        if (readiness !== 'ready') {
          result[readiness === 'variant' ? 'needsVariant' : 'unavailable'].push(product);
          continue;
        }
        try {
          await addToCart({
            locale,
            authToken,
            body: {
              item: { product: { id: product.id }, sub_items: { services: [], packages: [] } },
              qty: normalizeProductQuantity(1, product),
            },
          });
        } catch {
          result.failed.push(product);
          continue;
        }
        result.added.push(product);
        trackEcommerce.addToCart({
          id: String(product.id),
          name: product.name,
          price: getProductPriceDisplay(product).currentWithVat ?? 0,
          currency,
        });
      }
      return result;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.CART] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.SHIPMENTS] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.CART_PAYMENTS] });
    },
  });
}

function skippedGroups(result: AddAllResult) {
  return [
    { label: FAVORITES_COPY.cart.needsVariant, products: result.needsVariant },
    { label: FAVORITES_COPY.cart.unavailable, products: result.unavailable },
    { label: FAVORITES_COPY.cart.failed, products: result.failed },
  ].filter(group => group.products.length);
}

function addedText(result: AddAllResult) {
  return result.added.length
    ? FAVORITES_COPY.cart.added(productCount(result.added.length))
    : FAVORITES_COPY.cart.nothingAdded;
}

/** One sentence for the live region: what was added and what was left out. */
export function cartResultSummary(result: AddAllResult) {
  return [
    addedText(result),
    ...skippedGroups(result).map(
      group => `${group.label} ${group.products.map(product => product.name).join(', ')}.`,
    ),
  ].join(' ');
}

const LINK =
  'rounded-sm underline underline-offset-2 hover:text-[#D7191F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]';

/**
 * Result of „Pridať všetko do košíka“ in the look of the price alert (not in Figma): green when
 * everything was added, the warning tone with the left-out products (linked to their pages) when
 * something needs attention. Announced through the summary in the view's live region.
 */
export function CartResultNotice({
  result,
  onDismiss,
}: {
  result: AddAllResult;
  onDismiss: () => void;
}) {
  const { t } = useTranslations();
  const skipped = skippedGroups(result);

  return (
    <FavoritesNotice
      tone={skipped.length ? 'warning' : 'success'}
      icon={skipped.length ? 'warning' : 'check-circle'}
      action={
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('text272')}
          className="self-start rounded-full p-1 text-[#6B7079] transition-colors hover:text-[#0F1012] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
        >
          <Icon name="x" size={16} />
        </button>
      }
    >
      <p>
        {addedText(result)}
        {result.added.length ? (
          <>
            {' '}
            <Link href={ROUTES.CART()} prefetch={false} className={LINK}>
              {t('text456')}
            </Link>
          </>
        ) : null}
      </p>
      {skipped.map(group => (
        <p key={group.label} className="font-normal">
          {group.label}{' '}
          {group.products.map((product, index) => (
            <Fragment key={product.id}>
              {index ? ', ' : null}
              <Link href={getProductUrl(product, [])} prefetch={false} className={LINK}>
                {product.name}
              </Link>
            </Fragment>
          ))}
        </p>
      ))}
    </FavoritesNotice>
  );
}
